// Клиент для `/api/v1/self-improvement/proposals`.
//
// Зеркалит pydantic-схему backend'а `ProposalResponse`. Список
// возвращается объектом `{ proposals: [...] }` (pending сверху,
// сортировка на backend — UI не пересортировывает). apply/reject
// отдают обновлённое предложение голым объектом `ProposalResponse`
// (response_model=ProposalResponse, без обёртки).
//
// На не-ok ответе парсим `detail` из тела ошибки (FastAPI-формат
// `{ "detail": "..." }`), чтобы тост показал человекочитаемую причину
// 409 (не pending) / 422 (вид нельзя применить) / 404.

import { readErrorDetail } from "../../api/errorDetail";
import { authedFetch } from "../../auth/authedFetch";

// Виды предложений. Применяется только `prompt_tweak`; остальные виды
// backend отвергает с 422 (UI заранее дизейблит кнопку «Применить»).
export type ProposalKind =
  | "prompt_tweak"
  | "role_change"
  | "config_update"
  | "checklist_tweak"
  | "tool_blacklist";

export type ProposalStatus = "pending" | "applied" | "rejected" | "obsolete";

export interface Proposal {
  readonly id: string;
  readonly kind: ProposalKind;
  readonly target: string;
  readonly scope: string;
  readonly proposal_text: string;
  readonly confidence: number;
  readonly evidence_event_ids: readonly string[];
  readonly status: ProposalStatus;
  readonly created_at: string;
  readonly applied_at: string | null;
}

export interface ProposalsResponse {
  readonly proposals: readonly Proposal[];
}

function buildHeaders(): Record<string, string> {
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
}

// Достаёт человекочитаемую причину из FastAPI-тела ошибки
// (`{ "detail": "..." }`). Если тело не JSON или без `detail` —
// откатываемся на «<context>: HTTP <status>», чтобы тост никогда не
// был пустым.
async function errorFromResponse(
  response: Response,
  context: string,
): Promise<Error> {
  const detail = await readErrorDetail(response);
  if (detail !== null && detail.length > 0) {
    return new Error(detail);
  }
  return new Error(`${context}: HTTP ${String(response.status)}`);
}

export async function fetchProposals(): Promise<ProposalsResponse> {
  const response = await authedFetch("/api/v1/self-improvement/proposals", {
    headers: buildHeaders(),
  });
  if (!response.ok) {
    throw await errorFromResponse(response, "proposals");
  }
  return (await response.json()) as ProposalsResponse;
}

// Скаляр-значение конфиг-ключа (config_update): под схему ключа реестра —
// int/str → number/string, bool → boolean.
export type ConfigValue = number | string | boolean;

// Опции apply, подтверждённые человеком в UI:
//   role_change    → { model } (обязательно — 422 без неё);
//   config_update  → { configKey, configValue } (обязательны — 422 без них);
//   checklist_tweak → { repoFullName, checklistText } (обязательны — 422);
//   tool_blacklist → { roleName, toolName } (обязательны — 422);
//   prompt_tweak   → без опций (значение выводится из самого предложения).
export interface ApplyProposalOptions {
  readonly model?: string;
  readonly configKey?: string;
  readonly configValue?: ConfigValue;
  readonly repoFullName?: string;
  readonly checklistText?: string;
  readonly roleName?: string;
  readonly toolName?: string;
}

// Чистая сборка тела POST-запроса apply из опций. Вынесена отдельно,
// чтобы тестировать ветки напрямую (Mantine Select/NumberInput onChange в
// jsdom не зовётся). `prompt_tweak` (опций нет) → `null` (POST без тела);
// `role_change` → `{ model }`; `config_update` → `{ config_key,
// config_value }`; `checklist_tweak` → `{ repo_full_name, checklist_text }`;
// `tool_blacklist` → `{ role_name, tool_name }`.
// Зеркалит pydantic `ApplyProposalRequest`.
export function buildApplyBody(opts: ApplyProposalOptions): string | null {
  if (opts.model !== undefined) {
    return JSON.stringify({ model: opts.model });
  }
  if (opts.configKey !== undefined && opts.configValue !== undefined) {
    return JSON.stringify({
      config_key: opts.configKey,
      config_value: opts.configValue,
    });
  }
  if (opts.repoFullName !== undefined && opts.checklistText !== undefined) {
    return JSON.stringify({
      repo_full_name: opts.repoFullName,
      checklist_text: opts.checklistText,
    });
  }
  if (opts.roleName !== undefined && opts.toolName !== undefined) {
    return JSON.stringify({
      role_name: opts.roleName,
      tool_name: opts.toolName,
    });
  }
  return null;
}

// apply: тело зависит от вида (см. `buildApplyBody`). `null`-тело → POST
// без `body` (exactOptionalPropertyTypes запрещает явный undefined в
// опциональном поле, поэтому ключ опускаем).
export async function applyProposal(
  id: string,
  opts: ApplyProposalOptions = {},
): Promise<Proposal> {
  const body = buildApplyBody(opts);
  const response = await authedFetch(
    `/api/v1/self-improvement/proposals/${id}/apply`,
    {
      method: "POST",
      headers: buildHeaders(),
      ...(body === null ? {} : { body }),
    },
  );
  if (!response.ok) {
    throw await errorFromResponse(response, "applyProposal");
  }
  return (await response.json()) as Proposal;
}

// --- Реестр конфиг-ключей ----------------------
//
// Read-эндпоинт `GET /api/v1/self-improvement/config-keys` → список спеков
// ключей закрытого реестра `ConfigKey`. Источник вариантов для Select
// ключа и валидации значения на форме apply `config_update` (зеркало
// `GET /agent_roles/models` под role_change). `value_type` — строка
// `"int" | "bool" | "str"`; `min_value`/`max_value` — включительные
// границы для числовых ключей (или null).
export interface ConfigKeySpec {
  readonly key: string;
  readonly value_type: string;
  readonly default: ConfigValue;
  readonly min_value: number | null;
  readonly max_value: number | null;
}

export interface ConfigKeysResponse {
  readonly config_keys: readonly ConfigKeySpec[];
}

export async function fetchConfigKeys(): Promise<ConfigKeysResponse> {
  const response = await authedFetch("/api/v1/self-improvement/config-keys", {
    headers: buildHeaders(),
  });
  if (!response.ok) {
    throw await errorFromResponse(response, "configKeys");
  }
  return (await response.json()) as ConfigKeysResponse;
}

export async function rejectProposal(id: string): Promise<Proposal> {
  const response = await authedFetch(
    `/api/v1/self-improvement/proposals/${id}/reject`,
    {
      method: "POST",
      headers: buildHeaders(),
    },
  );
  if (!response.ok) {
    throw await errorFromResponse(response, "rejectProposal");
  }
  return (await response.json()) as Proposal;
}
